import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DocumentPreviewModal from '../ui/DocumentPreviewModal';
import type { CriminalDocumentRow } from '../../types/criminalCaseRequest';
import * as civilAdviseService from '../../services/civilAdviseCaseRequests';
import { setFlashToast } from '../../utils/flashToast';
import { formatReviewFileMeta } from '../../utils/dateFormat';
import { displayClientFileName } from '../../utils/fileDisplayName';

export type ReviewUploadedFileRow = {
  id: string;
  fileName: string;
  originalFileName: string;
  uploadTime?: string;
};

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function FileIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function toPreviewDoc(file: ReviewUploadedFileRow): CriminalDocumentRow {
  return {
    id: file.id,
    checklistItemId: '',
    documentTypeId: '',
    documentName: displayClientFileName(file.originalFileName, file.fileName),
    mandatory: false,
    fileName: file.fileName,
    originalFileName: file.originalFileName,
    uploadTime: file.uploadTime ?? '',
    skipReason: '',
  };
}

type Props = {
  files: ReviewUploadedFileRow[];
  emptyMessage?: string;
};

export function ReviewUploadedFilesTable({ files, emptyMessage }: Props) {
  const { t } = useTranslation();
  const [previewDoc, setPreviewDoc] = useState<CriminalDocumentRow | null>(null);

  const downloadFile = async (file: ReviewUploadedFileRow) => {
    const storageKey = file.fileName.trim() || file.originalFileName.trim();
    if (!storageKey) return;
    const r = await civilAdviseService.downloadCaseFile(storageKey);
    if (!r.success) {
      setFlashToast({ message: r.message ?? 'Download failed.', variant: 'error' });
      return;
    }
    const name = displayClientFileName(file.originalFileName, file.fileName);
    const url = URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  };

  const uploadedFiles = files.filter((f) => f.fileName.trim() || f.originalFileName.trim());
  if (!uploadedFiles.length) {
    return <p className="ccr-review-empty">{emptyMessage ?? t('common.noFilesUploaded')}</p>;
  }

  return (
    <>
      <div className="ccr-review-doc-wrap">
        <table className="ccr-review-doc-table">
          <thead>
            <tr>
              <th>File Name</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {uploadedFiles.map((file) => {
              const displayName = displayClientFileName(file.originalFileName, file.fileName);
              const fileMeta = formatReviewFileMeta(undefined, file.uploadTime);
              const previewRow = toPreviewDoc(file);

              return (
                <tr key={file.id}>
                  <td className="ccr-review-doc-type">{displayName}</td>
                  <td>
                    <span className="ccr-review-status is-uploaded">
                      <CheckIcon />
                      Uploaded
                    </span>
                  </td>
                  <td>
                    <div className="ccr-review-file-cell">
                      <span className="ccr-review-file-ico">
                        <FileIcon />
                      </span>
                      <div className="ccr-review-file-body">
                        <button
                          type="button"
                          className="ccr-review-file-name"
                          onClick={() => setPreviewDoc(previewRow)}
                        >
                          {displayName}
                        </button>
                        {fileMeta ? <span className="ccr-review-file-meta">{fileMeta}</span> : null}
                      </div>
                      <div className="ccr-review-file-acts">
                        <button
                          type="button"
                          className="ccr-review-file-act"
                          aria-label={`Preview ${displayName}`}
                          data-tip="Preview"
                          onClick={() => setPreviewDoc(previewRow)}
                        >
                          <EyeIcon />
                        </button>
                        <button
                          type="button"
                          className="ccr-review-file-act"
                          aria-label={`Download ${displayName}`}
                          data-tip="Download"
                          onClick={() => void downloadFile(file)}
                        >
                          <DownloadIcon />
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <DocumentPreviewModal
        isOpen={Boolean(previewDoc)}
        doc={previewDoc}
        onClose={() => setPreviewDoc(null)}
      />
    </>
  );
}
